import {Theme} from "@material-ui/core"
import ButtonBase from "@material-ui/core/ButtonBase"
import createStyles from "@material-ui/core/styles/createStyles"
import makeStyles from "@material-ui/core/styles/makeStyles"
import {CalendarToday} from "@material-ui/icons"
import {DateRange, DateRangePicker, DefinedRange} from "materialui-daterange-picker"
import * as React from "react"
import {AvantColors} from "../../../../../styles/AvantColors"
import {SmallBodyText} from "../../../../../styles/AvantTypography"
import {theme} from "../../../../../styles/MuiThemes"
import {log} from "../../../../../util/Logging"
import {NewDateInput} from "./AvantDatePickerTextBox"
// import {TimeUtil} from "../../../../../util/TimeUtil"
import moment from "moment"
import {AvantNewDateRangePickerHelpers, PlaceSearchFilters} from "./AvantNewDateRangePickerHelpers"
import Dialog from "@material-ui/core/Dialog"
import DialogTitle from "@material-ui/core/DialogTitle"
import Grid from "@material-ui/core/Grid"
import Button from "@material-ui/core/Button"
import {TimeUtil} from "../../../../../util/TimeUtil"

const AvantDatePickerTextBoxStyles = makeStyles((theme: Theme) =>
    createStyles({
        root_AvantDatePickerTextBox: {
            height: 37,
            width: 236,
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            marginLeft: theme.spacing(1)
        },
        dateRangeButtonDiv: {
            flexGrow: 1
        },
        button: {
            height: 33,
            width: 40,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "0px 4px 4px 0px"
        },
        dateRangePicker: {
            fontSize: 14,
            color: AvantColors.REPORT_FONT_COLOR_GRAY_1
        },
        dialogGridStyle: {
            padding: theme.spacing(1)
        }
    })
)

interface AvantDatePickerTextBoxProps {
    parentFilters: PlaceSearchFilters
    setParentDateRange: (range: DateRange) => void
}

export const AvantNewDatePicker: React.FC<AvantDatePickerTextBoxProps> = ({parentFilters, setParentDateRange}) => {

    const classes = AvantDatePickerTextBoxStyles()

    const buttonBaseRef = React.useRef(null)
    const [open, setOpen] = React.useState<boolean>(false)

    const existingDateRange = (): DateRange => {
        if (parentFilters.dateRangeFilters.startDate != null && parentFilters.dateRangeFilters.endDate != null) {
            const startDate = moment(parentFilters.dateRangeFilters.startDate).toDate()
            const endDate = moment(parentFilters.dateRangeFilters.endDate).toDate()
            return {startDate: startDate, endDate: endDate}
        } else {
            return {startDate: undefined, endDate: undefined}
        }
    }

    const now = new Date()
    const threeMonthsAgo: Date = moment().subtract(3, "months").toDate()
    const sixMonthsAgo: Date = moment().subtract(6, "months").toDate()
    const oneYearAgo: Date = moment().subtract(12, "months").toDate()
    const definedRanges: DefinedRange[] = [
        {startDate: threeMonthsAgo, endDate: now, label: "Last 3 Months"},
        {startDate: sixMonthsAgo, endDate: now, label: "Last 6 Months"},
        {startDate: oneYearAgo, endDate: now, label: "Last Year"}
    ]

    const focusHandler = () => {
        log.debug("Button Base Clicked")
        setOpen(true)
    }

    const changeRange = (range: DateRange) => {
        setParentDateRange(range)
        // log.debug(`Date Range: ${initialDateRange.startDate} to ${initialDateRange.endDate}`)
    }

    const applyDateRange = () => {
        log.debug("Apply clicked")
        setOpen(false)
    }

    /**
     * This method is not yet implemented, but will be usable for allowing user to input their dates by KB
     */
    const textChanger = (e: React.ChangeEvent<HTMLInputElement>) => {
        // setParentFilters(e)

        // TODO: Check if valid value, if so, update selected Date Range
        const result = AvantNewDateRangePickerHelpers.validateTextDateRange(e.target.value)

        if (result.valid) {
            log.debug("Got back a valid output")
            if (e.target.name === "DateInputStart") {
                // setSelectedDateRange({startDate: result.asDate})
                log.debug("Not yet implemented")
            }
            if (e.target.name === "DateInputEnd") {
                // setSelectedDateRange({endDate: result.asDate})
                log.debug("Not yet implemented")
            }

        } else {
            log.debug("Got back a INVALID output")
        }

    }

    const dateOutput = (range: DateRange) => {
        log.debug(`Date Range: ${range.startDate} to ${range.endDate}`)
        changeRange(range)
    }

    return (
        <div
            className={classes.root_AvantDatePickerTextBox}
            style={{
                marginTop: theme.spacing(1),
                border: open
                    ? `1px solid ${AvantColors.REPORT_BOX_BORDER_BLUE}`
                    : `1px solid ${AvantColors.REPORT_BOX_BORDER}`
            }}
        >
            <NewDateInput
                setParentFocus={setOpen}
                id={"DateInputStart"}
                name={"DateInputStart"}
                value={parentFilters.dateRangeFilters.startDate !== undefined
                    ? TimeUtil.formatMillisToUsDate(moment(parentFilters.dateRangeFilters.startDate).valueOf())
                    : ""
                }
                onChange={textChanger}
            />
            <SmallBodyText
                style={{marginRight: theme.spacing(1)}}
            >
                to
            </SmallBodyText>
            <NewDateInput
                setParentFocus={setOpen}
                id={"DateInputEnd"}
                name={"DateInputEnd"}
                value={parentFilters.dateRangeFilters.endDate !== undefined
                    ? TimeUtil.formatMillisToUsDate(moment(parentFilters.dateRangeFilters.endDate).valueOf())
                    : ""
                }
                onChange={textChanger}
            />
            <div className={classes.dateRangeButtonDiv}>
                <ButtonBase
                    className={classes.button}
                    ref={buttonBaseRef}
                    focusRipple={true}
                    onClick={focusHandler}
                    style={{
                        backgroundColor: open
                            ? AvantColors.REPORT_BACKGROUND_BLUE
                            : AvantColors.REPORT_BACKGROUND_GRAY_LIGHT
                    }}
                >
                    <CalendarToday color={open ? "primary" : "action"} style={{height: 16}}/>
                </ButtonBase>
            </div>
            <Dialog
                open={open}
                maxWidth={"md"}
                style={{textAlign: "center"}}
            >
                <DialogTitle>Date Range Filter</DialogTitle>
                <div>
                    <DateRangePicker
                        open={true}
                        toggle={focusHandler}
                        onChange={dateOutput}
                        definedRanges={definedRanges}
                        initialDateRange={existingDateRange()}
                    />
                </div>
                <div>
                    {/*<FormGroup>*/}
                        <Grid container={true} className={classes.dialogGridStyle}>
                            {/*<Grid*/}
                            {/*    item={true}*/}
                            {/*    xs={6}*/}
                            {/*>*/}
                            {/*    <FormControlLabel*/}
                            {/*        control={*/}
                            {/*            <Checkbox*/}
                            {/*                checked={parentFilters.dateRangeFilters.assessmentStart}*/}
                            {/*                onChange={setParentFilters}*/}
                            {/*                value={reportsAssessmentStartValue}*/}
                            {/*                name={reportsAssessmentStartValue}*/}
                            {/*            />*/}
                            {/*        }*/}
                            {/*        label={"Start Date"}*/}
                            {/*    />*/}
                            {/*</Grid>*/}
                            {/*<Grid*/}
                            {/*    item={true}*/}
                            {/*    xs={6}*/}
                            {/*>*/}
                            {/*    <FormControlLabel*/}
                            {/*        control={*/}
                            {/*            <Checkbox*/}
                            {/*                checked={parentFilters.dateRangeFilters.recentActivity}*/}
                            {/*                onChange={setParentFilters}*/}
                            {/*                value={reportsRecentActivityValue}*/}
                            {/*                name={reportsRecentActivityValue}*/}
                            {/*            />*/}
                            {/*        }*/}
                            {/*        label={"Recent Activity"}*/}
                            {/*    />*/}
                            {/*</Grid>*/}
                            <Grid item={true} xs={12}>
                                <Button
                                    onClick={applyDateRange}
                                    fullWidth={true}
                                    variant={"contained"}
                                    color={"primary"}
                                >
                                    Apply
                                </Button>
                            </Grid>
                        </Grid>
                    {/*</FormGroup>*/}
                </div>
            </Dialog>
        </div>
    )

}